import { useCallback, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { api } from '../../lib/api/client.js';
import {
  AudioControl,
  type AudioControlState,
} from '../../ui/components/AudioControl/AudioControl.tsx';
import { ChoiceTile, type ChoiceTileState } from '../../ui/components/ChoiceTile/ChoiceTile.tsx';
import { ProgressBar } from '../../ui/components/Progress/ProgressBar.tsx';
import { Brand } from '../../ui/components/Brand.tsx';
import { AmbientField } from '../../ui/primitives/AmbientField.tsx';
import { Badge } from '../../ui/primitives/Badge.tsx';
import { Button } from '../../ui/primitives/Button.tsx';
import { Icon, type IconName } from '../../ui/primitives/icon/index.ts';
import { Surface } from '../../ui/primitives/Surface.tsx';
import { Text } from '../../ui/primitives/Text.tsx';
import styles from './SignIn.module.css';

/**
 * `/entrar` — the whole of the platform's public face (T060, FR-027).
 *
 * `apps/api` is authoritative for authentication (research D-004): every "Continuar
 * com Google" control below hands the browser to the same Better Auth flow and
 * tells it where to come back to. There is no form, no password, and no session
 * state read here — `AuthProvider` re-resolves `/api/me` on return.
 *
 * This is a marketing page in front of a single control, not a set of
 * screens: the interactive listening exercise below is a DEMONSTRATION, not
 * the real practice loop — it holds its own local state and answers nothing
 * to the server. `PracticeShell` is where an Exercise is real.
 */

const DEMO_OPTIONS: readonly { label: string; correct: boolean }[] = [
  { label: 'Sol', correct: true },
  { label: 'Mi', correct: false },
  { label: 'Dó', correct: false },
  { label: 'Si', correct: false },
];

function demoTileState(index: number, picked: number | null): ChoiceTileState {
  if (picked === null) return 'default';
  if (DEMO_OPTIONS[index]!.correct) return 'resolved-correct';
  if (index === picked) return 'resolved-miss';
  return 'unchosen';
}

const HOW_IT_WORKS: readonly { step: string; title: string; body: string }[] = [
  {
    step: '01',
    title: 'Comece do zero',
    body: 'Escolha seu caminho e comece pelas primeiras lições.',
  },
  {
    step: '02',
    title: 'Pratique',
    body: 'Aprenda através de exercícios curtos e interativos.',
  },
  {
    step: '03',
    title: 'Evolua',
    body: 'Acompanhe seu progresso e continue avançando pelo caminho.',
  },
];

const WHY_IT_WORKS: readonly { title: string; body: string }[] = [
  { title: 'Pequenas sessões', body: 'Você não precisa passar horas estudando.' },
  { title: 'Prática ativa', body: 'Você aprende fazendo, ouvindo e respondendo.' },
  {
    title: 'Repetição inteligente',
    body: 'O conteúdo volta no momento certo para ajudar você a memorizar.',
  },
  { title: 'Progressão clara', body: 'Você sempre sabe o que estudar depois.' },
];

const FEATURES: readonly { icon: IconName; title: string; body: string }[] = [
  {
    icon: 'headphones',
    title: 'Aprenda ouvindo',
    body: 'Treine sua percepção musical através de exercícios de áudio.',
  },
  {
    icon: 'compass',
    title: 'Caminho guiado',
    body: 'Saiba exatamente o que estudar em seguida.',
  },
  {
    icon: 'alarm-clock',
    title: 'Lições rápidas',
    body: 'Estude em poucos minutos por dia.',
  },
  {
    icon: 'trending-up',
    title: 'Acompanhe sua evolução',
    body: 'Veja seu progresso ao longo do caminho.',
  },
  {
    icon: 'flame',
    title: 'Crie uma sequência',
    body: 'Transforme prática em hábito.',
  },
  {
    icon: 'gamepad',
    title: 'Exercícios interativos',
    body: 'Aprenda praticando, não apenas assistindo.',
  },
];

const STREAK_DAYS: readonly { value: string; label: string }[] = [
  { value: '4', label: 'seg' },
  { value: '3', label: 'ter' },
  { value: '—', label: 'qua' },
  { value: '5', label: 'qui' },
  { value: '2', label: 'sex' },
  { value: '6', label: 'sáb' },
];

const FAQ_ITEMS: readonly { question: string; answer: string }[] = [
  {
    question: 'Preciso saber tocar cavaquinho?',
    answer:
      'Não. O caminho começa pelos fundamentos, sem assumir nenhum conhecimento musical anterior.',
  },
  {
    question: 'Quanto tempo preciso estudar por dia?',
    answer: 'As lições são curtas e pensadas para caber na sua rotina — poucos minutos já bastam.',
  },
  {
    question: 'Posso estudar pelo celular?',
    answer: 'Sim, a experiência funciona bem em qualquer dispositivo.',
  },
  {
    question: 'Como faço para criar minha conta?',
    answer: 'Com apenas um clique, usando sua conta do Google.',
  },
  {
    question: 'Preciso ter conhecimento musical prévio?',
    answer: 'Não — o caminho foi pensado para quem está começando do zero.',
  },
];

export function SignIn() {
  const location = useLocation();
  const [error, setError] = useState(false);
  const [demoPicked, setDemoPicked] = useState<number | null>(null);
  const [faqOpen, setFaqOpen] = useState<number | null>(0);

  // The demo orb plays no real sound — there is nothing to fetch on a
  // pre-auth page — but it responds to a press the way the real one does,
  // rather than sitting inert. `playing` self-clears; there is no source to
  // report the sample's own length.
  const [demoAudioState, setDemoAudioState] = useState<AudioControlState>('idle');
  const demoAudioTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const playDemoAudio = useCallback(() => {
    if (demoAudioTimeout.current) clearTimeout(demoAudioTimeout.current);
    setDemoAudioState('playing');
    demoAudioTimeout.current = setTimeout(() => setDemoAudioState('idle'), 1400);
  }, []);

  // `RequireSession` (router.tsx) sends an anonymous visitor here carrying
  // `state: { from: location.pathname }`; /praticar is the fallback for a
  // direct visit to /entrar.
  const from = (location.state as { from?: string } | null)?.from ?? '/praticar';

  const handleSignIn = useCallback(() => {
    setError(false);
    const callbackURL = `${window.location.origin}${from}`;
    void api
      .signInWithGoogle(callbackURL)
      .then(({ url }) => {
        window.location.href = url;
      })
      .catch(() => {
        // Never a silent no-op: the learner pressed a button and nothing
        // happened is the one outcome this must not produce.
        setError(true);
      });
  }, [from]);

  const pickedCorrect = demoPicked !== null && DEMO_OPTIONS[demoPicked]!.correct;

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <Brand size="sm" mark />
        <nav className={styles.nav} aria-label="Seções da página">
          <a className={styles.navLink} href="#como-funciona">
            Como funciona
          </a>
          <a className={styles.navLink} href="#metodo">
            O método
          </a>
          <a className={styles.navLink} href="#recursos">
            Recursos
          </a>
          <a className={styles.navLink} href="#faq">
            FAQ
          </a>
        </nav>
        <Button variant="secondary" onClick={handleSignIn}>
          Continuar com Google
        </Button>
      </header>

      <main className={styles.main}>
        {/* HERO — § 8 L0's ambient field, anchored to the viewport since this
            screen sits outside AppLayout and carries its own ground. */}
        <section className={styles.hero}>
          <AmbientField count={2} anchor="viewport" />

          <div className={styles.heroCopy}>
            <Badge tone="neutral">Aprenda tocando, não só assistindo</Badge>
            <Text as="h1" variant="display-xl">
              Aprenda cavaquinho de um jeito simples, divertido e no seu ritmo.
            </Text>
            <Text as="p" variant="subheading" tone="muted">
              Lições rápidas, exercícios interativos e um caminho claro para você evoluir — mesmo
              começando do zero.
            </Text>

            <span data-testid="sign-in-google">
              <Button variant="primary-gradient" onClick={handleSignIn}>
                Continuar com Google
              </Button>
            </span>

            {error ? (
              <div data-testid="sign-in-failed">
                <Text as="p" variant="body-sm" tone="error">
                  Não foi possível iniciar o login. Tente novamente.
                </Text>
              </div>
            ) : null}
          </div>

          {/* A snapshot of the real practice loop, resolved to "Sol" so a
              visitor sees the answer a session gives without the page
              pretending it is one — the interactive version is in "O
              método" below. */}
          <div className={styles.heroPreview}>
            <Surface layer={2} radius="2xl" inset>
              <div className={styles.previewInner}>
                <ProgressBar total={6} current={1} label="Progresso da lição" />
                <AudioControl label="Ouvir a nota" statusLabel="Ouça e responda" />
                <Text as="p" variant="subheading">
                  Qual nota você ouviu?
                </Text>
                <div className={styles.previewGrid}>
                  {DEMO_OPTIONS.map((option, index) => (
                    <ChoiceTile
                      key={option.label}
                      label={option.label}
                      state={index === 0 ? 'resolved-correct' : 'unchosen'}
                    />
                  ))}
                </div>
              </div>
            </Surface>
          </div>
        </section>

        {/* TRUST STRIP */}
        <section className={styles.trustStrip} aria-label="Destaques">
          <Badge tone="neutral">Aprenda no seu ritmo</Badge>
          <Badge tone="neutral">Lições rápidas</Badge>
          <Badge tone="neutral">Exercícios interativos</Badge>
          <Badge tone="neutral">Do zero ao primeiro repertório</Badge>
        </section>

        {/* COMO FUNCIONA */}
        <section id="como-funciona" className={styles.section}>
          <div className={styles.sectionHeading}>
            <Text as="h2" variant="heading">
              Como funciona
            </Text>
            <Text as="p" variant="body-sm" tone="muted">
              Três passos simples para você começar a tocar.
            </Text>
          </div>
          <div className={styles.steps}>
            {HOW_IT_WORKS.map((item) => (
              <div key={item.step} className={styles.step}>
                <span className={styles.stepNumber} aria-hidden="true">
                  {item.step}
                </span>
                <Text as="h3" variant="subheading">
                  {item.title}
                </Text>
                <Text as="p" variant="body-sm" tone="muted">
                  {item.body}
                </Text>
              </div>
            ))}
          </div>
        </section>

        {/* O MÉTODO — the first of two dark panels this page allows itself,
            and its one genuinely interactive demonstration. */}
        <section id="metodo" className={styles.method}>
          <div className={styles.methodCopy}>
            <Badge tone="neutral">Exercício de identificação</Badge>
            <Text as="h2" variant="heading" tone="inverse">
              Você aprende ouvindo e respondendo, não assistindo.
            </Text>
            <Text as="p" variant="body" tone="inverse">
              Cada lição traz exercícios curtos onde você ouve uma nota, responde e recebe feedback
              na hora — assim a memória musical se forma pela prática, não pela repetição passiva.
            </Text>
          </div>

          <Surface layer={2} radius="xl" inset>
            <div className={styles.methodDemo}>
              <AudioControl
                label="Ouvir a nota"
                state={demoAudioState}
                statusLabel="Ouça e responda"
                onActivate={playDemoAudio}
              />
              <Text as="p" variant="subheading">
                Qual nota você ouviu?
              </Text>
              <div className={styles.previewGrid}>
                {DEMO_OPTIONS.map((option, index) => (
                  <ChoiceTile
                    key={option.label}
                    label={option.label}
                    state={demoTileState(index, demoPicked)}
                    onSelect={() => setDemoPicked(index)}
                  />
                ))}
              </div>
              {demoPicked !== null ? (
                <div className={styles.demoFeedback}>
                  <Text as="p" variant="body-sm" tone={pickedCorrect ? 'success' : 'near-miss'}>
                    {pickedCorrect ? 'Resposta certa!' : 'Quase — a nota era Sol.'}
                  </Text>
                  <button
                    type="button"
                    className={styles.demoReset}
                    onClick={() => setDemoPicked(null)}
                  >
                    Tentar outra nota
                  </button>
                </div>
              ) : null}
            </div>
          </Surface>
        </section>

        {/* O CAMINHO — a preview of the spine LessonsPage draws for real, at
            marketing weight: one Lesson open with its own run, the next one
            waiting below it. */}
        <section className={styles.section}>
          <div className={styles.sectionHeading}>
            <Text as="h2" variant="heading">
              O caminho
            </Text>
            <Text as="p" variant="body-sm" tone="muted">
              Cada lição abre exatamente onde você parou.
            </Text>
          </div>
          <div className={styles.path}>
            <div className={styles.pathCurrent}>
              <div className={styles.pathCurrentTitle}>
                <Text as="h3" variant="subheading">
                  Primeiros sons — cordas soltas
                </Text>
                <Badge>Em andamento</Badge>
              </div>
              <ProgressBar total={3} current={1} label="Progresso da lição atual" />
              <Text as="p" variant="body-sm" tone="muted">
                Identificação de nota · 3 exercícios
              </Text>
              <Button icon="play" onClick={handleSignIn}>
                Continuar no exercício 2
              </Button>
            </div>
            <div className={styles.pathNext}>
              <Text as="span" variant="body">
                As sete naturais
              </Text>
              <Text as="span" variant="numeric-inline" tone="muted">
                00 — 02
              </Text>
            </div>
          </div>
        </section>

        {/* POR QUE FUNCIONA */}
        <section className={styles.whySection}>
          <Text as="h2" variant="heading">
            Por que funciona
          </Text>
          <div className={styles.whyGrid}>
            {WHY_IT_WORKS.map((item) => (
              <div key={item.title}>
                <Text as="h3" variant="subheading">
                  {item.title}
                </Text>
                <Text as="p" variant="body-sm" tone="muted">
                  {item.body}
                </Text>
              </div>
            ))}
          </div>
        </section>

        {/* A SEQUÊNCIA — the second dark panel: a streak, shown here as a
            fixed example rather than live data. */}
        <section className={styles.streak}>
          <Badge>🔥 7 dias de sequência</Badge>
          <Text as="h2" variant="heading" tone="inverse">
            Você está criando o hábito.
          </Text>
          <div className={styles.streakDays}>
            {STREAK_DAYS.map((day) => (
              <div key={day.label} className={styles.streakDay}>
                <Text variant="numeric-display" tone="inverse">
                  {day.value}
                </Text>
                <Text variant="caption" tone="inverse">
                  {day.label}
                </Text>
              </div>
            ))}
          </div>
        </section>

        {/* FEITO PARA QUEM ESTÁ COMEÇANDO */}
        <section className={styles.beginners}>
          <Text as="h2" variant="heading">
            Nunca tocou cavaquinho? Tudo bem.
          </Text>
          <Text as="p" variant="body-sm" tone="muted">
            O caminho começa pelos fundamentos, sem assumir nenhum conhecimento musical anterior.
          </Text>
          <div className={styles.progression}>
            <Badge tone="neutral">Zero conhecimento</Badge>
            <span className={styles.progressionArrow} aria-hidden="true">
              →
            </span>
            <Badge tone="neutral">Primeiras notas</Badge>
            <span className={styles.progressionArrow} aria-hidden="true">
              →
            </span>
            <Badge tone="neutral">Primeiros acordes</Badge>
            <span className={styles.progressionArrow} aria-hidden="true">
              →
            </span>
            <span className={styles.progressionFinal}>
              <Badge>Primeira música</Badge>
            </span>
          </div>
        </section>

        {/* RECURSOS */}
        <section id="recursos" className={styles.section}>
          <Text as="h2" variant="heading">
            Tudo que você precisa para evoluir
          </Text>
          <div className={styles.featureGrid}>
            {FEATURES.map((feature) => (
              <Surface key={feature.title} layer={1} radius="lg" inset>
                <div className={styles.feature}>
                  <span className={styles.featureIcon}>
                    <Icon name={feature.icon} size={20} />
                  </span>
                  <Text as="h3" variant="subheading">
                    {feature.title}
                  </Text>
                  <Text as="p" variant="body-sm" tone="muted">
                    {feature.body}
                  </Text>
                </div>
              </Surface>
            ))}
          </div>
        </section>

        {/* FAQ */}
        <section id="faq" className={styles.faqSection}>
          <Text as="h2" variant="heading">
            Perguntas frequentes
          </Text>
          <div className={styles.faqList}>
            {FAQ_ITEMS.map((item, index) => {
              const open = faqOpen === index;
              const answerId = `faq-answer-${index}`;
              return (
                <Surface key={item.question} layer={1} radius="lg">
                  <button
                    type="button"
                    className={styles.faqTrigger}
                    aria-expanded={open}
                    aria-controls={answerId}
                    onClick={() => setFaqOpen((current) => (current === index ? null : index))}
                  >
                    <Text as="span" variant="subheading">
                      {item.question}
                    </Text>
                    <span
                      className={styles.faqChevron}
                      data-open={open || undefined}
                      aria-hidden="true"
                    >
                      <Icon name="chevron-down" size={16} />
                    </span>
                  </button>
                  {open ? (
                    <p id={answerId} className={styles.faqAnswer}>
                      <Text variant="body-sm" tone="muted">
                        {item.answer}
                      </Text>
                    </p>
                  ) : null}
                </Surface>
              );
            })}
          </div>
        </section>

        {/* FINAL CTA — the § 4 gradient-border recipe over an opaque core,
            same construction as "O caminho"'s open Lesson above. */}
        <section className={styles.finalCta}>
          <div className={styles.finalCtaInner}>
            {/* `heading`, not `display-lg` — § 5.2 caps a screen at two
                display levels, and the hero above already spends the
                page's one on `display-xl`. The animated border is what
                gives this moment its weight instead. */}
            <Text as="h2" variant="heading">
              Seu cavaquinho começa aqui.
            </Text>
            <Text as="p" variant="subheading" tone="muted">
              Alguns minutos por dia. Um passo de cada vez.
            </Text>
            <Button variant="primary" onClick={handleSignIn}>
              Continuar com Google
            </Button>
          </div>
        </section>
      </main>

      <footer className={styles.footer}>
        <Brand size="sm" mark />
        <Text as="span" variant="body-sm" tone="muted">
          © 2026 cavaquinho
        </Text>
      </footer>
    </div>
  );
}
